import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import DatalistInput from './DatalistInput';
import PaymentHistoryEditor from './PaymentHistoryEditor';
import {
    API_BASE,
    authHeaders,
    emptyPaymentRow,
    formatTelegramLabel,
    inputClassName,
    toDateInputValue,
} from '../../utils/assetHelpers';

const REDIRECT_WEB = 'Redirect Web';
const REDIRECT_TELEGRAM = 'Redirect Telegram';

const emptyForm = {
    domainURL: '',
    status: '',
    redirectTo: '',
    server: '',
    subServer: '',
    DNS: '',
    DNSAccount: '',
    expiryDate: '',
    autoRenew: false,
    inCloudflare: false,
    loginID: '',
    loginPass: '',
    remarks: '',
    paymentHistory: [],
};

const DomainAssetModal = ({ mode = 'create', asset = null, meta = {}, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({ ...emptyForm, paymentHistory: [emptyPaymentRow()] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const statuses = meta.statuses || [];
    const dnsOptions = meta.domain?.dns || [];
    const dnsAccountOptions = meta.domain?.dnsAccounts || [];
    const currencyOptions = meta.domain?.currencies || [];
    const servers = meta.servers || [];
    const domainAssets = meta.domainAssets || [];
    const telegramAssets = meta.telegramAssets || [];

    const isRedirectWeb = formData.status === REDIRECT_WEB;
    const isRedirectTelegram = formData.status === REDIRECT_TELEGRAM;

    const redirectDomainOptions = useMemo(() => {
        const currentId = mode === 'edit' ? asset?._id : null;
        return domainAssets.filter((row) => String(row._id) !== String(currentId));
    }, [domainAssets, mode, asset?._id]);

    const subServerOptions = useMemo(() => {
        const selected = servers.find((s) => s._id === formData.server);
        return selected?.subServerObject || [];
    }, [servers, formData.server]);

    useEffect(() => {
        if (mode === 'edit' && asset) {
            setFormData({
                domainURL: asset.domainURL || '',
                status: asset.status || '',
                redirectTo: asset.redirectTo?._id || asset.redirectTo || '',
                server: asset.server?._id || asset.server || '',
                subServer: asset.subServer || asset.linkedSubServer?._id || '',
                DNS: asset.DNS || '',
                DNSAccount: asset.DNSAccount || '',
                expiryDate: toDateInputValue(asset.expiryDate),
                autoRenew: Boolean(asset.autoRenew),
                inCloudflare: Boolean(asset.inCloudflare),
                loginID: asset.loginID || '',
                loginPass: asset.loginPass || '',
                remarks: asset.remarks || '',
                paymentHistory: asset.paymentHistory?.length
                    ? asset.paymentHistory.map((row) => ({
                        currency: row.currency || '',
                        amount: row.amount ?? '',
                        paymentDate: toDateInputValue(row.paymentDate),
                    }))
                    : [emptyPaymentRow()],
            });
        }
    }, [mode, asset]);

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleServerChange = (serverId) => {
        const server = servers.find((s) => s._id === serverId);
        const validSubIds = (server?.subServerObject || []).map((row) => row._id);
        setFormData((prev) => ({
            ...prev,
            server: serverId,
            subServer: validSubIds.includes(prev.subServer) ? prev.subServer : '',
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                domainURL: formData.domainURL.trim(),
                status: formData.status.trim(),
                redirectTo: (isRedirectWeb || isRedirectTelegram) ? (formData.redirectTo || null) : null,
                server: formData.server || null,
                subServer: formData.server && formData.subServer ? formData.subServer : null,
                DNS: formData.DNS.trim(),
                DNSAccount: formData.DNSAccount.trim(),
                expiryDate: formData.expiryDate || null,
                autoRenew: formData.autoRenew,
                inCloudflare: formData.inCloudflare,
                loginID: formData.loginID.trim(),
                loginPass: formData.loginPass,
                remarks: formData.remarks,
                paymentHistory: formData.paymentHistory
                    .map((row) => ({
                        currency: row.currency.trim(),
                        amount: row.amount === '' ? 0 : Number(row.amount),
                        paymentDate: row.paymentDate || null,
                    }))
                    .filter((row) => row.currency || row.amount || row.paymentDate),
            };

            const request = mode === 'edit'
                ? axios.put(`${API_BASE}/api/assets/domains/${asset._id}`, payload, { headers: authHeaders() })
                : axios.post(`${API_BASE}/api/assets/domains`, payload, { headers: authHeaders() });

            const response = await request;
            if (response.data.success) onSuccess(response.data.data);
        } catch (err) {
            setError(`Failed to ${mode} domain: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col min-h-0 shadow-lg">
                <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-gray-200 shrink-0">
                    <h2 className="text-xl font-bold">{mode === 'edit' ? 'Edit Domain Asset' : 'Create Domain Asset'}</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
                        {error && <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Domain URL</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.domainURL}
                                    onChange={(e) => updateField('domainURL', e.target.value)}
                                    className={inputClassName}
                                />
                            </div>
                            <DatalistInput
                                id="domain-status-list"
                                label="Status"
                                value={formData.status}
                                onChange={(value) => updateField('status', value)}
                                options={statuses}
                            />
                            {isRedirectWeb && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Redirect To Domain</label>
                                    <select
                                        value={formData.redirectTo}
                                        onChange={(e) => updateField('redirectTo', e.target.value)}
                                        className={inputClassName}
                                        required
                                    >
                                        <option value="">Select domain</option>
                                        {redirectDomainOptions.map((row) => (
                                            <option key={row._id} value={row._id}>
                                                {row.domainURL || row._id}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {isRedirectTelegram && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Redirect To Telegram</label>
                                    <select
                                        value={formData.redirectTo}
                                        onChange={(e) => updateField('redirectTo', e.target.value)}
                                        className={inputClassName}
                                        required
                                    >
                                        <option value="">Select telegram asset</option>
                                        {telegramAssets.map((row) => (
                                            <option key={row._id} value={row._id}>
                                                {formatTelegramLabel(row)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Server</label>
                                <select
                                    value={formData.server}
                                    onChange={(e) => handleServerChange(e.target.value)}
                                    className={inputClassName}
                                >
                                    <option value="">No server linked</option>
                                    {servers.map((server) => (
                                        <option key={server._id} value={server._id}>
                                            {server.mainServerName || server._id}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Sub Server</label>
                                <select
                                    value={formData.subServer}
                                    onChange={(e) => updateField('subServer', e.target.value)}
                                    className={inputClassName}
                                    disabled={!formData.server}
                                >
                                    <option value="">
                                        {formData.server ? 'Main server only' : 'Select a server first'}
                                    </option>
                                    {subServerOptions.map((sub) => (
                                        <option key={sub._id} value={sub._id}>
                                            {sub.name || 'Unnamed'}
                                            {sub.type ? ` (${sub.type})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <DatalistInput
                                id="domain-dns-list"
                                label="DNS"
                                value={formData.DNS}
                                onChange={(value) => updateField('DNS', value)}
                                options={dnsOptions}
                            />
                            <DatalistInput
                                id="domain-dns-account-list"
                                label="DNS Account"
                                value={formData.DNSAccount}
                                onChange={(value) => updateField('DNSAccount', value)}
                                options={dnsAccountOptions}
                            />
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                                <input
                                    type="date"
                                    value={formData.expiryDate}
                                    onChange={(e) => updateField('expiryDate', e.target.value)}
                                    className={inputClassName}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Login ID</label>
                                <input
                                    type="text"
                                    value={formData.loginID}
                                    onChange={(e) => updateField('loginID', e.target.value)}
                                    className={inputClassName}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Login Password</label>
                                <input
                                    type="text"
                                    value={formData.loginPass}
                                    onChange={(e) => updateField('loginPass', e.target.value)}
                                    className={inputClassName}
                                />
                            </div>
                            <div className="flex items-center gap-6 pt-6 md:col-span-2">
                                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={formData.autoRenew}
                                        onChange={(e) => updateField('autoRenew', e.target.checked)}
                                        className="rounded border-gray-300"
                                    />
                                    Auto Renew
                                </label>
                                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={formData.inCloudflare}
                                        onChange={(e) => updateField('inCloudflare', e.target.checked)}
                                        className="rounded border-gray-300"
                                    />
                                    In Cloudflare
                                </label>
                            </div>
                        </div>

                        <PaymentHistoryEditor
                            key={`${mode}-${asset?._id || 'new'}`}
                            rows={formData.paymentHistory}
                            onChange={(rows) => updateField('paymentHistory', rows)}
                            currencyOptions={currencyOptions}
                        />

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                            <textarea
                                value={formData.remarks}
                                onChange={(e) => updateField('remarks', e.target.value)}
                                rows={3}
                                className={`${inputClassName} font-normal text-gray-800`}
                            />
                        </div>
                    </div>

                    <div className="shrink-0 px-6 py-4 border-t border-gray-200 bg-white flex justify-end gap-3">
                        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                            {loading ? 'Saving...' : mode === 'edit' ? 'Update Domain' : 'Create Domain'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DomainAssetModal;
